import type { PropertyRegistrationForm } from '../types/property.types';

import { toNumber } from '@/features/companies/utils/mappers/supplier-form-mapper';

/**
 * Common utilities for property mappers
 * Contains shared logic for both create and update operations
 */

/**
 * Convert customer ID from form (string | number) to API format (string)
 */
export const convertCustomerId = (customerId: string | number): string => {
  if (typeof customerId === 'string' && customerId !== '') {
    return customerId;
  }
  if (typeof customerId === 'number') {
    return customerId.toString();
  }
  return '';
};

/**
 * Convert construction company ID from form (string | number) to API format (string)
 */
export const convertConstructionCompanyId = (constructionCompanyId: string | number): string => {
  if (typeof constructionCompanyId === 'string' && constructionCompanyId !== '') {
    return constructionCompanyId;
  }
  if (typeof constructionCompanyId === 'number') {
    return constructionCompanyId.toString();
  }
  return '';
};

/**
 * Convert staff code from form (string | number) to API format (number | null)
 */
export const convertStaffCode = (code: string | number): number | null => {
  if (typeof code === 'string' && code !== '') {
    return Number.parseInt(code, 10);
  }
  if (typeof code === 'number') {
    return code;
  }
  return null;
};

/**
 * Check if an item has a database ID (not a temporary UI ID)
 */
export const hasDbId = (item: { id?: unknown }): boolean => {
  return 'id' in item && Boolean(item.id) && !String(item.id).startsWith('temp_');
};

/**
 * Base property request payload - contains all common fields between create and update
 */
export interface BasePropertyRequestPayload {
  // Basic Information
  waoPropertyCode?: string;
  name: string;
  nameAbbreviation: string;
  postalCodeFirst?: number;
  postalCodeLast?: number;
  address: string;
  startDate: string;
  ownerName?: string;
  publicPrivateClassificationCode?: number;
  customerId: string;
  constructionCompanyId: string;
  constructionPeriodFrom?: string;
  constructionPeriodTo?: string;
  officeAddress?: string;
  officeTel?: string;
  officeFax?: string;
  structuralFormatCode?: number;
  structuralFormatOthers?: string;
  numberOfFloorsAboveGround?: string;
  numberOfBasementFloors?: string;
  siteManagerName?: string;
  siteManagerTel?: string;
  rmcContractQuantity?: number;
  vehicleSize?: number;
  // Nohara Representatives
  departmentCode: number;
  salesRepresentativeCode: number | null;
  deliveryPersonCode: number | null;
  // Information Input Section
  achievementConfirmationCompany?: string;
  noharaDirect?: string;
  pWindow?: string;
  sellersOtherGc?: string;
  checkMethod?: number;
  memo?: string;
  resultsCaution?: string;
  jvPrice?: number;
  generalContractorPurchasePrice?: number;
  existsPurchaseOrder?: boolean;
  cooperative?: string;
  linkCementCode?: number;
  managerComment?: string;
}

/**
 * Map common fields from PropertyRegistrationForm to BasePropertyRequestPayload
 * Used by both create and update mappers
 */
export const mapCommonFields = (
  data: PropertyRegistrationForm,
  canManagePrice: boolean
): BasePropertyRequestPayload => {
  // Convert IDs
  const customerId = convertCustomerId(data.customer_id);
  const constructionCompanyId = convertConstructionCompanyId(data.construction_company_id);

  // Convert staff codes
  const salesRepCode = convertStaffCode(data.sales_representative_code);
  const deliveryPersonCode = convertStaffCode(data.delivery_person_code);

  return {
    // Basic Information
    waoPropertyCode: data.wao_property_code || '',
    name: data.property_name,
    nameAbbreviation: data.property_name_abbreviation,
    postalCodeFirst: toNumber(data.postal_code_first_half),
    postalCodeLast: toNumber(data.postal_code_latter_half),
    address: data.address,
    startDate: data.start_date,
    ownerName: data.owner_name || undefined,
    publicPrivateClassificationCode: data.public_private_classification_code || undefined,
    customerId,
    constructionCompanyId,
    constructionPeriodFrom: data.construction_period_from || undefined,
    constructionPeriodTo: data.construction_period_to || undefined,
    officeAddress: data.office_address || undefined,
    officeTel: data.office_tel || undefined,
    officeFax: data.office_fax || undefined,
    structuralFormatCode: data.structural_format_code || undefined,
    structuralFormatOthers: data.structural_format_others || undefined,
    numberOfFloorsAboveGround: data.number_of_floors_above_ground || undefined,
    numberOfBasementFloors: data.number_of_basement_floors || undefined,
    siteManagerName: data.ready_mix_concrete_staff_name || undefined,
    siteManagerTel: data.ready_mix_concrete_staff_tel || undefined,
    rmcContractQuantity: data.ready_mixed_concrete_contract_quantity || undefined,
    vehicleSize: data.vehicle_size || undefined,

    // Nohara Representatives
    departmentCode: data.department_code,
    salesRepresentativeCode: salesRepCode,
    deliveryPersonCode: deliveryPersonCode,

    // Information Input Section
    achievementConfirmationCompany: data.achievement_confirmation_company || undefined,
    noharaDirect: data.nohara_direct || undefined,
    pWindow: data.p_window || undefined,
    sellersOtherGc: data.sellers_other_gc || undefined,
    checkMethod: data.check_method || undefined,
    memo: data.memo || undefined,
    resultsCaution: data.results_caution || undefined,
    managerComment: data.manager_comment || undefined,
    ...(canManagePrice && {
      jvPrice: data.jv_price || undefined,
      generalContractorPurchasePrice: data.general_contractor_purchase_price || undefined,
      existsPurchaseOrder: data.exists_purchase_order ?? false,
      cooperative: data.cooperative || undefined,
      linkCementCode: data.link_cement_code ?? undefined,
    }),
  };
};
